import { Box, Button, IconButton, List, ListItem, Stack, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as HistoryTitleIcon }  from '../../../assets/solar_history-bold_title.svg';
import { timestampCal } from 'utils/common-util';

interface QuerySearchHistoryProps {
    setIsQuerySearchHistoryOpen: any;
	setQuery: any;
	setIsReapplied: any;
	setQueryPreference: any;
	setSelectedCheckbox: any;
	setSelGroupPaths: any;
	isProjectDetails: any;
	setSavedQuery: any;
	setIsHistoryQuery: any;
}

export const queryPrefOptions = [
	{
		label: 'Resource Groups',
		value: 'ResourceGroup_tree'
	},
	{
		label: 'Resources',
		value: 'Resource_flat'
	},
	{
		label: 'DBSQL Clusters',
		value: 'SqlEndpointId_tree'
	},
	{
		label: 'Interactive Clusters',
		value: 'ClusterName_inttree'
	},
	{
		label: 'Job Clusters',
		value: 'ClusterJobSource_jobtree'
	},
	{
		label: 'Blank Query',
		value: 'Blank_flatall'
	}
];

const QuerySearchHistory: React.FC<QuerySearchHistoryProps> = ({ 
	setIsQuerySearchHistoryOpen, 
	setQuery, 
	setIsReapplied, 
	setQueryPreference, 
	setSelectedCheckbox, 
	setSelGroupPaths,
	isProjectDetails,
	setSavedQuery,
	setIsHistoryQuery
	}: any) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [searchHistory, setSearchHistory] = useState<any[]>([]);
	const [, setLoading] = useState<boolean>(false);
	const [remainingCount, setRemainingCount] = useState(0);
	
	function queryParam(query: any) {
		let paramIndex = 0;
		return query.sql.replace(/\?/g, () => {
		  const param = query.params[paramIndex++];
		  return typeof param === 'string' ? `'${param}'` : param;
		});
	}

	function getQueryPrefLabel(queryPreference: string) {
		const match = queryPrefOptions.find(option => option.value === queryPreference);
		return match ? match.label : '';
	};

	const handleReapply = (selItem: any) => {
		if(!!isProjectDetails) setSavedQuery({});
		setIsHistoryQuery(true);
		setQuery(selItem.rawQuery);
		setQueryPreference(selItem.queryPreference ?? 'Resource_flat');
		setIsReapplied(true);
    	setSelectedCheckbox(selItem.selectedCheckbox); 
    	setSelGroupPaths(selItem.selGroupPaths); 
		let selectedColumns = selItem.fields;
		if (typeof selectedColumns === 'string') {
			selectedColumns = selectedColumns.split(',').map(col => col.trim());
		}
		const baseColumns = Array.isArray(selectedColumns) ? selectedColumns : [];
		const updatedSelectedColumns = [
			"Actions",
			"HeaderCheckboxMapping",
			...baseColumns.filter(col => col !== "Actions" && col !== "HeaderCheckboxMapping")
		];
		const storageKey = isProjectDetails ? 'resource-details_user_col_pref' : 'resource-list_user_col_pref';
		localStorage.setItem(storageKey, JSON.stringify(updatedSelectedColumns));
		setIsQuerySearchHistoryOpen(false); 
	};

	const handleDelete = async (delItem: any) => {
		try {
			const updatedHistory = searchHistory.filter((item) => {
				return !(
				  item.query.sql === delItem.query.sql &&
				  JSON.stringify(item.query.params) === JSON.stringify(delItem.query.params) &&
				  JSON.stringify(item.fields) === JSON.stringify(delItem.fields)
				);
			  });
			localStorage.setItem('query_search_history', JSON.stringify(updatedHistory));
			setSearchHistory(updatedHistory);
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (!scrollRef.current) return;
		const scrollBox = scrollRef.current;
		const items = scrollBox.querySelectorAll('.search-item');
		let visibleCount = 0;
		const boxTop = scrollBox.scrollTop;
		const boxBottom = boxTop + scrollBox.clientHeight;
		items.forEach((item: any) => {
			const itemTop = item.offsetTop;
			const itemBottom = itemTop + item.offsetHeight;
	
			// Check if the item is even partially visible
			if (itemBottom > boxTop && itemTop < boxBottom) {
				visibleCount++;
			}
		});
		setRemainingCount(searchHistory.length - visibleCount);
	}, [searchHistory]);

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [showAll, setShowAll] = useState(true);
	const [isScrollable, setIsScrollable] = useState(false);

	useEffect(() => {
		const el = scrollRef.current;
		if (el) {
		  setIsScrollable(el.scrollHeight > el.clientHeight);
		}
	}, [searchHistory, showAll]);

	//fetching data
	useEffect(() => {
		setIsReapplied(false);
		let queryHistory: any = localStorage.getItem('query_search_history')
		if (queryHistory) {
			const parsedData = queryHistory ? JSON.parse(queryHistory) : [];
			setLoading(true);
			if (Array.isArray(parsedData)) 
				setSearchHistory(parsedData);
			else
				setSearchHistory([])
		}
	}, []);

	return (
		<Stack direction="column" p={4} position='relative' sx={{backgroundColor: `${colors.overlay.background} !important`, color: `${colors.overlay.title} !important`, height: '100%'}}>
			<Stack minWidth='250px'
				direction="row"
				spacing={1}
				pb={2}
				marginBottom={1}
				justifyContent="space-between"
				borderBottom={`0.5px solid ${colors.overlay.border} !important`}
			>
				<Stack direction="row" alignItems="flex-start" spacing={1}
					sx={{
						position: 'absolute',
						top: 15,
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<HistoryTitleIcon className='close-icon' style={{ width: '24px', height: '24px', color: `${colors.overlay.IconColor}` }}/>
					<Typography fontSize="16px" sx={{color: `${colors.overlay.title} !important`, fontWeight: 500 }}>Recent Query Searches</Typography>
				</Stack>
				<IconButton
					onClick={() => setIsQuerySearchHistoryOpen(false)} // Close drawer
					sx={{
						position: 'absolute',
						top: 8,
						right: 6,
						color: `${colors.overlay.title} !important`,
						'& svg': {
							color: `${colors.overlay.IconColor} !important`,
							fill: `${colors.overlay.IconColor} !important`
						},
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<CloseIcon sx={{color: `${colors.overlay.IconColor} !important`, fill: `${colors.overlay.IconColor} !important`}}/>
				</IconButton>
			</Stack>
			<Box ref={scrollRef}
				sx={{
					maxHeight: 'calc(100vh - 115px)',
					overflowY: showAll ? 'auto' : 'hidden',
				}}
				>
				<List>
					{searchHistory.length === 0 && (
						<Typography mt={2} ml={2} sx={{ color: `${colors.queryBuilder.text} !important`}}>No recent queries found.</Typography>
					)}
					{searchHistory.map((item, index) => (
					<ListItem className="search-item"
					key={item.id}
					alignItems="flex-start"
					sx={{ flexDirection: 'column', 
						alignItems: 'flex-start', 
						padding: '8px 25px 0px 0px', 
						width: '100%'}}
					>
						<Stack
							sx={{ width: '100%',
								backgroundColor: `${colors.queryBuilder.cardBackground} !important`,
								color: `${colors.queryBuilder.text} !important`,
								padding: 1.5,
								borderRadius: 1,
								mb: 0.5,
								display: 'flex',
								alignItems:"flex-start"
							}}>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="flex-start"
								sx={{ width: '100%' }}
								>
								<Typography
										sx={{
										flex: 1,
										whiteSpace: 'pre-wrap',
										wordBreak: 'break-word',
										pr: 2, 
										color: `${colors.queryBuilder.text} !important`,
										}}
									>
										<Box component="span" sx={{ display: 'inline', whiteSpace: 'nowrap'  }}>
											<Box component="span" sx={{ color: `${colors.queryBuilder.primary} !important` }}>
												Query Preference:{' '}
											</Box>
											<Box component="span" sx={{ color: `${colors.queryBuilder.text} !important` }}>{getQueryPrefLabel(item.queryPreference)}</Box>
										</Box>
										<Box component="div" sx={{ color: `${colors.queryBuilder.primary} !important`, marginTop: '2px' }}>
											Where{' '}
										</Box>
										<Box component="div" sx={{ display: 'block', ml: 2, color: `${colors.queryBuilder.text} !important` }}>
											{queryParam(item.query)}
										</Box>
								</Typography>
								<Typography sx={{ color: `${colors.queryBuilder.subText} !important`, whiteSpace: 'pre-line', textAlign: 'right', flexShrink: 0 }}>
									{timestampCal(new Date(item.timestamp))}
								</Typography>
							</Stack>
							<Stack  direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: 1.5 }}>
								<Box display="flex" gap={1}>
									<Button sx={{border: `1px solid ${colors.queryBuilder.primary}`, color: `${colors.queryBuilder.primary} !important`, fontWeight: '700',
										textTransform: 'uppercase', minWidth: 80, py: 0.5 }} variant="pepwiseSecondaryOutline2"
										onClick={() => handleDelete(item)}>
										Delete
									</Button>
									<Button variant="pepwiseSecondary"  sx={{ minWidth: 90, py: 0.5  }} onClick={() => handleReapply(item)}>
										Reapply
									</Button>
								</Box>
							</Stack>
						</Stack>
					</ListItem>
				))}
				</List>
			</Box>
			{isScrollable && remainingCount > 0 && (
				<Typography textAlign="left" sx={{ cursor: 'pointer', color: `${colors.queryBuilder.subText} !important`, margin: '10px 0px 0px 0px', lineHeight: 1 }}
					onClick={() => {
						setShowAll(prev => {
						const newState = !prev;
						// Scroll to top only when collapsing
						if (!newState && scrollRef.current) {
							scrollRef.current.scrollTop = 0;
						}
						return newState;
						});
					}}>
					{showAll ? 'Show Less' : `Showing Last ${remainingCount} Results`}
				</Typography>
			)}
		</Stack>
	);
};

export default QuerySearchHistory;
